import LogoWithText from "@/assets/svg/logo-full.svg";
import { useUser } from "@/hooks/user";
import { useUnreadNotificationStatus } from "@/lib/backend/notifications";
import { usePayments } from "@/lib/backend/payments";
import {
	getSubscriptionLabel,
	hadPremiumSubscription,
	hasPremiumSubscription,
} from "@/lib/backend/utils";
import { m } from "@/paraglide/messages.js";
import { Button } from "@bidbattles/bloks/components/ui/button";
import { Divider } from "@bidbattles/bloks/components/ui/divider";
import { Link } from "@bidbattles/bloks/components/ui/link";
import {
	Popover,
	PopoverAction,
	PopoverContent,
	PopoverDescription,
	PopoverTitle,
	PopoverTrigger,
} from "@bidbattles/bloks/components/ui/popover";
import { BBBackendClient } from "@bidbattles/bloks/lib/rapace";
import { cn } from "@bidbattles/bloks/lib/ui";
import { isServer, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClientOnly, useNavigate } from "@tanstack/react-router";
import { createServerOnlyFn } from "@tanstack/react-start";
import { getRequestUrl } from "@tanstack/react-start/server";
import { Suspense } from "react";
import { toast } from "sonner";
import {
	Bell01,
	HelpCircle,
	Home01,
	LinkExternal01,
	LogOut04,
} from "untitledui-js/react";
import { AuthDialog } from "./auth.dialog";
import { PremiumUpsellModal } from "./premium-upsell";
import { Avatar } from "./user";

const getRequestUrlOnServer = createServerOnlyFn(() =>
	getRequestUrl({ xForwardedHost: true, xForwardedProto: true }).toString(),
);

export function Header() {
	const user = useUser();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const logoutMutation = useMutation({
		mutationFn: async () => {
			const toastId = toast.loading("Logging out...");
			const result = await BBBackendClient.logout();
			if (!result.ok) {
				toast.dismiss(toastId);
				toast.error(`An error occured: ${result.error.message}`);
				return;
			}

			toast.dismiss(toastId);
			toast.success("Logged out.");

			queryClient.invalidateQueries({
				predicate: () => true,
			});
			navigate({
				to: "/",
			});
		},
	});

	const payments = usePayments();
	const crmUrl = new URL(
		"/",
		isServer ? getRequestUrlOnServer() : window.location.href,
	);
	crmUrl.hostname = `crm.${crmUrl.hostname}`;

	return (
		<header
			id="top"
			className="flex justify-center items-center border-b border-gray-warm-200"
		>
			<nav className="p-box flex flex-row gap-2 max-w-container w-full justify-between items-center">
				<Link to="/">
					<img src={LogoWithText} className="w-40" alt={m.header__home()} />
				</Link>

				<div className="flex items-center gap-4">
					<Link to="/help-center">
						<HelpCircle size="1em" />
					</Link>
					<AuthDialog
						trigger={<Button size="sm">{m.header__login()}</Button>}
						hidden={!!user}
					/>

					{user && (
						<>
							{!hasPremiumSubscription(user.subscription) && (
								<PremiumUpsellModal
									payments={payments}
									trigger={
										<Button className="relative" size="sm">
											💎{" "}
											<span className="sr-only lg:not-sr-only">
												{hadPremiumSubscription(user.subscription)
													? m.upsell__pls_come_back()
													: m.upsell__get_premium()}
											</span>
										</Button>
									}
								/>
							)}

							<Popover>
								<PopoverTrigger
									render={
										<Button variant="icon" size="icon" className="relative">
											<Avatar user={user} />
											{hasPremiumSubscription(user.subscription) && (
												<span
													aria-hidden
													className="absolute bottom-0 end-0 translate-x-1/4 translate-y-1/4 text-sm"
												>
													💎
												</span>
											)}
											<ClientOnly>
												<Suspense fallback={null}>
													<UnreadNotificationBadge className="absolute top-0 end-0" />
												</Suspense>
											</ClientOnly>
										</Button>
									}
								/>
								<PopoverContent
									className="pt-4 overflow-hidden w-[15rem] max-w-full"
									align="end"
								>
									<div className="px-4 pb-4">
										<PopoverTitle>
											{user.first_name} {user.last_name}
										</PopoverTitle>
										<PopoverDescription>
											{user.email} • {getSubscriptionLabel(user.subscription)}
										</PopoverDescription>
									</div>
									<Divider />
									<div className="w-full flex flex-col items-stretch">
										{user.role === "admin" && (
											<PopoverAction
												className="cursor-pointer focus-visible:outline-none focus-visible:bg-gray-300 hover:bg-gray-warm-100 inline-flex items-center px-4 py-2 gap-4 text-sm text-gray-900"
												render={
													<a
														href={crmUrl.toString()}
														target="_blank"
														rel="noreferrer"
													/>
												}
											>
												<LinkExternal01 stroke="currentColor" size="1em" />
												Go to CRM
											</PopoverAction>
										)}
										<PopoverAction
											className="cursor-pointer focus-visible:outline-none focus-visible:bg-gray-300 hover:bg-gray-warm-100 inline-flex items-center px-4 py-2 gap-4 text-sm text-gray-900"
											render={<Link to="/settings" />}
										>
											<Home01 stroke="currentColor" size="1em" />
											Dashboard
										</PopoverAction>
										<PopoverAction
											className="cursor-pointer focus-visible:outline-none focus-visible:bg-gray-300 hover:bg-gray-warm-100 inline-flex items-center px-4 py-2 gap-4 text-sm text-gray-900"
											render={<Link to="/settings/notifications" />}
										>
											<span className="relative">
												<Bell01 stroke="currentColor" size="1em" />
												<ClientOnly>
													<Suspense fallback={null}>
														<UnreadNotificationBadge className="absolute top-0 end-0" />
													</Suspense>
												</ClientOnly>
											</span>
											Notifications
										</PopoverAction>
										<PopoverAction
											className="cursor-pointer focus-visible:outline-none focus-visible:bg-gray-300 hover:bg-gray-warm-100 inline-flex items-center px-4 py-2 gap-4 text-sm text-gray-900"
											disabled={logoutMutation.isPending}
											onClick={() => logoutMutation.mutate()}
										>
											<LogOut04 stroke="currentColor" size="1em" />
											{m.auth__log_out()}
										</PopoverAction>
									</div>
								</PopoverContent>
							</Popover>
						</>
					)}
				</div>
			</nav>
		</header>
	);
}

function UnreadNotificationBadge({ className }: { className?: string }) {
	const hasUnreadNotifs = useUnreadNotificationStatus();
	if (!hasUnreadNotifs) {
		return null;
	}

	return (
		<div className={cn("size-2 rounded-full bg-accent-primary", className)}>
			<span className="sr-only">You have unread notifications.</span>
		</div>
	);
}
